/**
 * RequestsBrowser Component
 *
 * Displays a filterable, sortable list of requests split into priority groups.
 *
 * This component handles its own filtering logic in addition to what's provided by QueueContext
 * to ensure consistent behavior with the mobile implementation. It directly filters requests by the
 * current status filter before showing them, ensuring the correct subset is displayed regardless of
 * how the context data is structured.
 */

import './RequestsBrowser.scss';
import { prefixClass } from 'lib/utils';
import AssignmentGroup from '../AssignmentGroup';

import { FilterProps } from '../utils/filterAndSearchUtils';
import { useRef, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import RBSearch from './RBSearch';
import { useQueueContext } from '../QueueContext';
import { IconCalendarQueue, IconCalendarQueueUp, IconMentionDisabled, IconMentionPurple } from 'components/Icons';
import { useQueryState } from '../../../../hooks/useQueryState';
import { useAuth } from 'context/useAuthContext';
import classNames from 'classnames';
import { Tooltip, Whisper } from 'rsuite';

const RequestsBrowser = () => {
  const prefix = prefixClass('requests-browser');

  const { userId } = useAuth();
  const resultsRef = useRef<HTMLDivElement>(null);
  const [filterMentions, setFilterMentions] = useQueryState('filterMentions');
  const [requestId] = useQueryState('requestId');
  const [sort, setSort] = useQueryState('sort');
  const [status] = useQueryState('status');
  const [searchParams] = useSearchParams();
  // Add a ref to track if the request selection was triggered by a manual click
  const manualSelectionRef = useRef<boolean>(false);
  const {
    state: { filters, requests, filteredNormalRequests, priorityGroups, newRequestAdded },
    dispatch,
  } = useQueueContext();

  const handleSearch = (value: string) => {
    dispatch({
      type: 'SET_FILTERS',
      payload: { ...filters, search: value },
    });
  };
  const handleClear = () => {
    dispatch({
      type: 'SET_FILTERS',
      payload: { ...filters, search: '' },
    });
  };

  const updateFilters = (newFilter: string) => {
    dispatch({ type: 'SET_FILTERS', payload: { ...filters, sort: newFilter } as FilterProps });
  };

  const handleRequestClick = (requestId: string) => {
    const request = requests.find(r => r.id === requestId);
    if (request) {
      // Set the selected item in the context
      dispatch({ type: 'SET_SELECTED_ITEM', payload: request });

      // Set the manual selection flag to true to prevent auto-scrolling
      manualSelectionRef.current = true;

      // Update the URL to include the requestId parameter
      const newParams = new URLSearchParams(searchParams);
      newParams.set('requestId', requestId);

      // Add status parameter to URL if it's different from current status
      // This ensures the URL always reflects the correct status for the selected request
      if (request.status && searchParams.get('status') !== request.status) {
        // Type assertion to ensure request status is a valid FilterProps['status']
        const validStatus = request.status as 'Open' | 'In Progress' | 'Closed';

        // Also update the filters in context to match URL
        dispatch({
          type: 'SET_FILTERS',
          payload: {
            ...filters,
            status: validStatus,
            // Update sort order based on status as well
            sort: validStatus === 'Closed' ? 'newest' : filters.sort,
          },
        });
      }
    }
  };

  // Effect to scroll to bottom when a new request is added and we're in oldest-first mode
  useEffect(() => {
    if (newRequestAdded && filters.sort === 'oldest' && resultsRef.current) {
      // Delay the scroll to ensure the DOM has been updated
      setTimeout(() => {
        if (resultsRef.current) {
          resultsRef.current.scrollTop = resultsRef.current.scrollHeight;
        }

        // Reset the newRequestAdded flag so we don't scroll again on subsequent renders
        dispatch({ type: 'SET_NEW_REQUEST_ADDED', payload: null });
      }, 500);
    }
  }, [newRequestAdded, filters.sort]);

  // Effect to sync URL status parameter with context filters on mount
  useEffect(() => {
    const statusFromUrl = searchParams.get('status');

    // Update context filters if URL has a valid status that's different from current
    if (
      statusFromUrl &&
      ['Open', 'In Progress', 'Closed'].includes(statusFromUrl) &&
      statusFromUrl !== filters.status
    ) {
      // Type assertion to ensure statusFromUrl is treated as a valid FilterProps['status']
      const validStatus = statusFromUrl as 'Open' | 'In Progress' | 'Closed';

      dispatch({
        type: 'SET_FILTERS',
        payload: {
          ...filters,
          status: validStatus,
          sort: validStatus === 'Closed' ? 'newest' : 'oldest',
        },
      });
    }
    // Only run on mount
  }, []);
  const requestIdFromUrl = requestId;

  // Effect to handle requestId in URL - scroll to and highlight the selected request
  useEffect(() => {
    // Only proceed if requestId exists in URL and requests are loaded
    if (requestIdFromUrl && requests && requests.length > 0) {
      // Find the request in the context's list
      const statusFromUrl = searchParams.get('status');
      const requestFromContext = requests.find(req => req.status === statusFromUrl);

      // If found and it's not already the selected item, dispatch action
      if (requestFromContext) {
        // Set the selected item in context
        // dispatch({ type: 'SET_SELECTED_ITEM', payload: requestFromContext });

        // Also ensure the status in the URL and filters match the request's status
        if (requestFromContext.status && statusFromUrl !== requestFromContext.status) {
          // Type assertion to ensure request status is a valid FilterProps['status']
          const validStatus = statusFromUrl as 'Open' | 'In Progress' | 'Closed';

          // Update filters in context
          dispatch({
            type: 'SET_FILTERS',
            payload: {
              ...filters,
              status: validStatus,
              sort: validStatus === 'Closed' ? 'newest' : 'oldest',
            },
          });
        }

        // Add CSS for highlight animation if it doesn't exist
        if (!document.getElementById('highlight-animation-style')) {
          const style = document.createElement('style');
          style.id = 'highlight-animation-style';
          style.innerHTML = `
              .highlight-request {
                animation: pulse 0.1s;
              }
              @keyframes pulse {
                0% { background-color: var(--ks-dashboard-selected-item); }
                100% { background-color: transparent; }
              }
            `;
          document.head.appendChild(style);
        }

        // Only scroll if this wasn't triggered by a manual click
        if (!manualSelectionRef.current) {
          // Scroll to the selected request after a short delay
          setTimeout(() => {
            // Find the request item by its data attribute
            const requestElement = document.querySelector(`[data-request-id="${requestIdFromUrl}"]`);

            if (requestElement && resultsRef.current) {
              // Calculate position to ensure the element is visible in the scrollable container
              const containerRect = resultsRef.current.getBoundingClientRect();
              const elementRect = requestElement.getBoundingClientRect();

              // Scroll the container to bring the element into view
              resultsRef.current.scrollTop =
                resultsRef.current.scrollTop +
                elementRect.top -
                containerRect.top -
                containerRect.height / 2 +
                elementRect.height / 2;

              // Add a temporary highlight class
              requestElement.classList.add('highlight-request');

              // Remove the highlight class after animation completes
              setTimeout(() => {
                requestElement.classList.remove('highlight-request');
              }, 100);
            }
          }, 200);
        } else {
          // Reset the manual selection flag for next time
          manualSelectionRef.current = false;

          // Still add the highlight effect without scrolling
          setTimeout(() => {
            const requestElement = document.querySelector(`[data-request-id="${requestIdFromUrl}"]`);
            if (requestElement) {
              requestElement.classList.add('highlight-request');
              setTimeout(() => {
                requestElement.classList.remove('highlight-request');
              }, 200);
            }
          }, 50);
        }
      }
    }
  }, [requestIdFromUrl, searchParams, requests]);

  /**
   * Component-level filtering logic to ensure we always show the correct requests.
   *
   * While QueueContext provides priorityGroups, we handle filtering directly at the component level
   * to ensure consistency between mobile and desktop implementations and avoid issues where:
   * 1. The priorityGroups structure might contain requests with mixed statuses
   * 2. URL parameters and context state might get out of sync during navigation
   *
   * This approach guarantees that what's shown always matches the current selected status tab.
   */
  // Use the already filtered and priority-split data from the context
  // This ensures search results are properly displayed
  // Filter blocker requests by the current status to prevent accumulation when changing status
  const blockerRequests = (priorityGroups?.Blocker || []).filter(req => req.status === filters.status);

  // Ensure normalRequests doesn't contain any requests that are already in blockerRequests
  // by filtering out any request whose ID matches a blocker request ID
  const blockerRequestIds = blockerRequests.map(req => req.id);
  const normalRequests = (filteredNormalRequests || []).filter(req => !blockerRequestIds.includes(req.id));

  const mentionedRequest = requests.filter(
    req => req.status === filters.status && req.comments?.some(comment => comment.taggedUserIds?.includes(userId!))
  );

  const handleFilterMentions = () => {
    setFilterMentions(`${filterMentions === 'true' ? 'false' : 'true'}`);
  };

  const handleSortRequests = () => {
    const newFilter = filters.sort === 'newest' ? 'oldest' : 'newest';
    updateFilters(newFilter);
  };

  const sortTooltip = filters.sort
    ? filters.sort === 'newest'
      ? 'Sorted by Newest'
      : 'Sorted by Oldest'
    : status !== 'Closed'
      ? 'Sorted by Oldest'
      : 'Sorted by Newest';
  return (
    <div className={prefix()}>
      <div className={prefix('search-header')}>
        <RBSearch searchValue={filters.search || ''} onSearch={handleSearch} onClear={handleClear} />
        <div className={prefix('search-header-actions')}>
          <button onClick={handleFilterMentions} className={prefix('search-header-action-button')}>
            {filterMentions === 'true' ? <IconMentionPurple /> : <IconMentionDisabled />}
          </button>
          <Whisper
            placement="top"
            controlId="control-id-hover"
            trigger="hover"
            speaker={<Tooltip>{sortTooltip}</Tooltip>}
          >
            <button
              onClick={handleSortRequests}
              className={classNames(prefix('search-header-action-button'), { active: sort === 'newest' })}
            >
              {filters.sort === 'newest' ? <IconCalendarQueue /> : <IconCalendarQueueUp />}
            </button>
          </Whisper>
        </div>
      </div>

      {/* AssignmentGroups by priority */}
      {/* Search and Action Bar */}
      <div className={prefix('action-bar')}>
        {/*         <div className={prefix('search')}>
          <RBSearch searchValue={filters.search || ''} onSearch={handleSearch} onClear={handleClear} />
        </div> */}
        {/*  <div className={prefix('action-buttons')}>
          <RBFilter filter={filters} onFilterChange={updateFilters} />

          <button
                  type="button"
                  onClick={updateFilters}
                  className={prefix('action-button sort')}
                  aria-label="Sort Requests"
                >
                  <IconSortTime />
                </button>
                <button
                  type="button"
                  onClick={toggleCreateRequestModal}
                  className={prefix('action-button')}
                  aria-label="Create Request"
                >
                  <IconAddNew />
                </button>
        </div> */}
      </div>
      <div ref={resultsRef} className={prefix('results')}>
        {/* We don't have assignment in the API once that's implemented we can now bring this ones back */}
        {/*<AssignmentGroup*/}
        {/*  me*/}
        {/*  requests={splitRequests?.me || []}*/}
        {/*  reversed={filters.sort === 'newest'}*/}
        {/*  onClick={handleRequestClick}*/}
        {/*/>*/}
        {filterMentions === 'true' ? (
          <AssignmentGroup
            me={false}
            requests={mentionedRequest}
            reversed={filters.sort === 'newest'}
            onClick={handleRequestClick}
          />
        ) : (
          <>
            <AssignmentGroup
              me={false}
              blocker
              requests={blockerRequests}
              reversed={filters.sort === 'newest'}
              onClick={handleRequestClick}
            />
            <AssignmentGroup
              me={false}
              requests={normalRequests}
              reversed={filters.sort === 'newest'}
              onClick={handleRequestClick}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default RequestsBrowser;
